import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import React from "react";
import { closeModal as closeModalLibrary } from "risa-oasis-ui_v2";
import { FormMatrixTableDataModel } from "../../../../../../../data-model/tablesData";
import { ModalId } from "../../../../../../../enums/modalId";
import {
  closeModal as closeModalRedux,
  setOpenedModalId,
} from "../../../../../../../redux/slice/modalSliceNew";
import { AppDispatch } from "../../../../../../../redux/store/store";
import type { FormMatrixConfigItem } from "../hooks/useFormMatrixData";
import {
  FIELD_TO_COLUMN_KEY,
  TOGGLE_SUFFIX_TO_FIELD,
} from "./formMatrixConstants";

export interface ToggleEmitData {
  name: string;
  required: boolean;
  value: boolean;
}

export interface ParsedToggleData {
  rowIndex: number;
  field: keyof FormMatrixConfigItem;
  value: boolean;
}

export const parseToggleEmitData = (
  data: ToggleEmitData,
): ParsedToggleData | null => {
  const parts = data.name.split("-");
  if (parts.length < 4) return null;
  const rowIndex = parseInt(parts[2], 10);
  if (Number.isNaN(rowIndex)) return null;
  const fieldSuffix = parts[3];
  const field = TOGGLE_SUFFIX_TO_FIELD[fieldSuffix];
  if (!field) return null;
  return { rowIndex, field, value: data.value };
};

export const updateWorkingDataWithToggle = (
  prevData: FormMatrixTableDataModel[],
  rowIndex: number,
  field: keyof FormMatrixConfigItem,
  value: boolean,
): FormMatrixTableDataModel[] => {
  const colKey = FIELD_TO_COLUMN_KEY[field];
  const next = [...prevData];
  const row = next[rowIndex];
  if (!row || !row[colKey] || typeof row[colKey] !== "object") return prevData;
  next[rowIndex] = {
    ...row,
    [colKey]: {
      ...(row[colKey] as { id: string; defaultChecked: boolean }),
      defaultChecked: value,
    },
  };
  return next;
};

export const handleToggleChange = (
  data: ToggleEmitData,
  setWorkingTableData: React.Dispatch<
    React.SetStateAction<FormMatrixTableDataModel[]>
  >,
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>,
): void => {
  const parsed = parseToggleEmitData(data);
  if (!parsed) return;
  setWorkingTableData((prev) =>
    updateWorkingDataWithToggle(
      prev,
      parsed.rowIndex,
      parsed.field,
      parsed.value,
    ),
  );
  setIsDirty(true);
};

export const handleSave = async (
  workingTableData: FormMatrixTableDataModel[],
  mapTableRowToConfigItem: (
    row: FormMatrixTableDataModel,
  ) => FormMatrixConfigItem,
  saveFormMatrixData: (data: FormMatrixConfigItem[]) => Promise<void>,
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>,
  setSaving: React.Dispatch<React.SetStateAction<boolean>>,
): Promise<void> => {
  setSaving(true);
  try {
    const configData = workingTableData.map(mapTableRowToConfigItem);
    await saveFormMatrixData(configData);
    setIsDirty(false);
    toast.success("Form matrix saved successfully.");
  } catch {
    toast.error("Failed to save. Please try again.");
  } finally {
    setSaving(false);
  }
};

export const handleDiscard = (
  tableData: FormMatrixTableDataModel[],
  setWorkingTableData: React.Dispatch<
    React.SetStateAction<FormMatrixTableDataModel[]>
  >,
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>,
): void => {
  setWorkingTableData(tableData);
  setIsDirty(false);
};

export const handleAddFormMatrixSave = async (
  formData: FormMatrixConfigItem,
  saveNewFormMatrixItem: (item: FormMatrixConfigItem) => Promise<void>,
  setSaving: React.Dispatch<React.SetStateAction<boolean>>,
  onSuccess: () => void,
): Promise<void> => {
  setSaving(true);
  try {
    await saveNewFormMatrixItem(formData);
    toast.success("Form Added Successfully");
    onSuccess();
  } catch {
    toast.error("Failed to add form. Please try again.");
  } finally {
    setSaving(false);
  }
};

export const handleAddFormClick = (dispatch: AppDispatch): void => {
  dispatch(setOpenedModalId({ id: ModalId.ADD_FORM_MATRIX_DIALOG }));
};

export const closeAddFormModal = (
  setAddFormKey: React.Dispatch<React.SetStateAction<number>>,
  dispatch: AppDispatch,
): void => {
  setAddFormKey((k) => k + 1);
  closeModalLibrary(ModalId.ADD_FORM_MATRIX_DIALOG);
  dispatch(closeModalRedux());
};

export const handleAddFormCancel = (
  setSaving: React.Dispatch<React.SetStateAction<boolean>>,
  setAddFormKey: React.Dispatch<React.SetStateAction<number>>,
  dispatch: AppDispatch,
): void => {
  setSaving(false);
  closeAddFormModal(setAddFormKey, dispatch);
};

interface ButtonMeta {
  text: string;
  onClick: () => void;
  disabled: boolean;
  buttonType: "primary" | "secondary" | "tertiary";
  size: "small" | "medium" | "large";
}

export const getAddFormButtonMeta = (onClick: () => void): ButtonMeta[] => [
  {
    text: "Add Form",
    onClick,
    disabled: false,
    buttonType: "primary",
    size: "medium",
  },
];
